import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@appcharge/shared-ui";
import { ChevronDown } from "lucide-react";

import Checkbox from "components/Checkbox/Checkbox";
import { CheckbokItem } from "components/FilterComponents/CheckList/CheckbokItem";
import { Popover } from "components/Popover";

export interface ProjectSelectItem {
    label: string;
    value: string;
}

export interface ProjectSelectProps {
    items: ProjectSelectItem[];
    selectedItems: string[];
    onChange: (value: string) => void;
    onBulkChange?: (values: string[]) => void;
    label?: string;
    placeholder?: string;
    searchPlaceholder?: string;
}

export const ProjectSelect = ({
    items,
    selectedItems,
    onChange,
    onBulkChange,
    label,
    placeholder = "Select projects",
    searchPlaceholder = "Search..."
}: ProjectSelectProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const selectRef = useRef<HTMLDivElement>(null);

    const displayText = useMemo(() => {
        if (!selectedItems || selectedItems.length === 0) {
            return placeholder;
        }
        return `${selectedItems.length} selected`;
    }, [selectedItems, placeholder]);

    const filteredItems = useMemo(() => {
        if (!searchTerm) return items;

        const lowerSearch = searchTerm.toLowerCase();
        return items.filter(item =>
            item.label.toLowerCase().includes(lowerSearch) ||
            item.value.toLowerCase().includes(lowerSearch)
        );
    }, [items, searchTerm]);

    const allSelected = useMemo(() => {
        return filteredItems.length > 0 && filteredItems.every(item => selectedItems.includes(item.value));
    }, [filteredItems, selectedItems]);

    const handleSelectAll = useCallback(() => {
        if (onBulkChange) {
            // Use bulk change if available
            if (allSelected) {
                // Remove all filtered items from selection
                const filteredValues = filteredItems.map(item => item.value);
                const newSelection = selectedItems.filter(id => !filteredValues.includes(id));
                onBulkChange(newSelection);
            } else {
                // Add all filtered items to selection
                const filteredValues = filteredItems.map(item => item.value);
                const valuesToAdd = filteredValues.filter(val => !selectedItems.includes(val));
                const newSelection = [...selectedItems, ...valuesToAdd];
                onBulkChange(newSelection);
            }
        } else {
            // Fallback to individual toggles
            const itemsToToggle = allSelected
                ? filteredItems.filter(item => selectedItems.includes(item.value))
                : filteredItems.filter(item => !selectedItems.includes(item.value));

            itemsToToggle.forEach((item) => {
                onChange(item.value);
            });
        }
    }, [allSelected, filteredItems, selectedItems, onChange, onBulkChange]);

    // Close dropdown when clicking outside
    useEffect(() => {
        if (!isOpen) return;

        const listener = (event: MouseEvent) => {
            if (selectRef.current?.contains(event.target as Node)) {
                return;
            }
            setIsOpen(false);
        };

        const timeoutId = setTimeout(() => {
            document.addEventListener('click', listener);
        }, 0);

        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener('click', listener);
        };
    }, [isOpen]);

    const handleToggle = useCallback(() => {
        setIsOpen(!isOpen);
    }, [isOpen]);

    const trigger = (
        <Button
            variant="outline"
            onClick={handleToggle}
            className="w-full justify-between"
            style={{ width: '342px' }}
        >
            <span className={`font-normal ${selectedItems.length === 0 ? "text-muted-foreground" : ""}`}>
                {displayText}
            </span>
            <ChevronDown
                className={`transition-transform duration-200 ${isOpen ? "transform rotate-180" : ""}`}
            />
        </Button>
    );

    return (
        <div ref={selectRef} className="relative w-full">
            {label && (
                <label className="block text-sm font-medium text-base-primary mb-2">
                    {label}
                </label>
            )}

            <Popover
                width="100%"
                height="auto"
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                trigger={trigger}
            >
                <div className="flex flex-col rounded-md bg-base-popover text-xs pb-1">
                    {/* Search Input */}
                    <div className="relative">
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder={searchPlaceholder}
                            className="w-full h-10 px-3 pr-10 border-b border-base-input bg-base-background text-base-foreground placeholder:text-base-foreground/50 focus:outline-none outline-none"
                        />
                    </div>

                    {/* Select All Checkbox */}
                    <div className="pl-3 py-2 border-b border-base-input">
                        <Checkbox
                            id="select-all"
                            className="[&[data-state=checked]]:bg-indigo-500 [&[data-state=indeterminate]]:bg-indigo-500"
                            label="Select all"
                            checked={allSelected}
                            onCheckedChange={handleSelectAll}
                        />
                    </div>

                    {/* Items List */}
                    <div className="flex flex-col gap-2 max-h-[200px] overflow-y-auto ml-2 mt-3">
                        {filteredItems.length > 0 ? (
                            filteredItems.map(item => (
                                <CheckbokItem
                                    key={item.value}
                                    item={item}
                                    selectedItems={selectedItems}
                                    onChange={onChange}
                                />
                            ))
                        ) : (
                            <div className="text-center text-xs text-base-foreground/50">
                                No results found
                            </div>
                        )}
                    </div>
                </div>
            </Popover>
        </div>
    );
};

export default ProjectSelect;

